import { Ref, useCallback, useImperativeHandle } from "react";
import DecisionWithModal from "@coworker/apprestructured/src/task-management/components/DecisionWithModal/DecisionWithModal";
import SafetyAlarmInfoModalContent from "@coworker/apprestructured/src/task-management/components/TaskInfoPanel/TaskInfoModal/SafetyAlarmInfoModalContent";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { validateBooleanType } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import { Trans, useTranslation } from "@coworker/locales";
import { FixaSegmentedControl } from "@coworker/apprestructured/src/shared/wrappers/FixaSegmentedControl/FixaSegmentedControl";
import InformationCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/InformationCircleIconPath";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import styles from "./SafetyAlarmCreatorFragment.module.css";

export type SafetyAlarmCreatorFragmentConfigProps = {
  field: string;
};

type SafetyAlarmCreatorFragmentProps = {
  configProps: SafetyAlarmCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function SafetyAlarmCreatorFragment({
  configProps,
  commonProps,
  ref,
}: SafetyAlarmCreatorFragmentProps) {
  const { field } = configProps;
  const { blockInput, task, onTaskChange } = commonProps;
  const { t } = useTranslation();
  const checked = validateBooleanType(getByPath(task, field)) ?? false;

  useImperativeHandle(ref, () => ({ valid: true }));

  const handleDecisionChange = useCallback(
    (_: unknown, index: number) => onTaskChange(setByPath(field, index === 0)),
    [field, onTaskChange]
  );

  return (
    <section className={styles["root"]}>
      <DecisionWithModal
        title={t("safetyAlarmLearnMoreString")}
        leadingIcon={InformationCircleIconPath}
        modalContent={({ visible, onClose }) => (
          <SafetyAlarmInfoModalContent visible={visible} onClose={onClose} />
        )}
      >
        <div className={styles["decision"]}>
          <FixaText tagName="h2" textStyle="Heading.L">
            {t("safetyAlarmString")}
          </FixaText>
          <FixaText textStyle="Body.L" className={styles["prompt"]}>
            <Trans>safetyAlarmCheckboxHeaderString</Trans>
          </FixaText>
          <ul className={styles["label"]}>
            <li>{t("safetyAlarmCheckboxLine1String")}</li>
            <li>{t("safetyAlarmCheckboxLine2String")}</li>
            <li>
              {t("safetyAlarmCheckboxLine3String")}{" "}
              {t("safetyAlarmCheckboxLine4String")}
            </li>
          </ul>
          <FixaSegmentedControl
            className={styles["segmentedControl"]}
            items={[
              { label: t("yesString"), type: "button" },
              { label: t("noString"), type: "button" },
            ]}
            activeIndex={isLoading(checked) ? 1 : checked ? 0 : 1}
            size="medium"
            disabled={blockInput || isLoading(checked)}
            onClick={handleDecisionChange}
          />
        </div>
      </DecisionWithModal>
    </section>
  );
}

export default SafetyAlarmCreatorFragment;
